import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { FiCalendar, FiTag, FiPackage, FiPercent, FiDollarSign, FiX, FiSearch, FiChevronDown, FiAlignLeft, FiLoader } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { api } from '../../../api/api';

const CreateOffer = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProductSelectorOpen, setIsProductSelectorOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    offerType: 'PERCENTAGE',
    value: '',
    applicableTo: 'product',
    selectedItems: [],
    startDate: null,
    endDate: null,
  });

  const [products, setProducts] = useState([]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const productsData = await api.getProducts();
        setProducts(productsData);
      } catch (error) {
        toast.error('Failed to load products');
        console.error('Error loading products:', error);
      }
    };

    loadProducts();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!formData.name || !formData.value || !formData.startDate || !formData.endDate) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.offerType === 'PERCENTAGE' && (parseFloat(formData.value) < 0 || parseFloat(formData.value) > 100)) {
        throw new Error('Percentage must be between 0 and 100');
      }

      // Convert dates to ISO string format
      const startDate = formData.startDate.toISOString();
      const endDate = formData.endDate.toISOString();

      await api.createOffer({
        ...formData,
        startDate,
        endDate,
      });
      
      toast.success('Offer created successfully!');
      navigate('/admin/offers');
    } catch (error) {
      toast.error(error.message || 'Failed to create offer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProductToggle = (productId) => {
    setFormData(prev => ({
      ...prev,
      selectedItems: prev.selectedItems.includes(productId)
        ? prev.selectedItems.filter(id => id !== productId)
        : [...prev.selectedItems, productId]
    }));
  };

  const getSelectedProducts = () => {
    return products.filter(product => formData.selectedItems.includes(product.id));
  };

  // Filter products based on search term
  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-4xl mx-auto px-4 py-8"
    >
      <Helmet>
        <title>Create Offer | Admin Dashboard</title>
      </Helmet>

      {/* Header with modern styling */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-800">Create New Offer</h1>
        <p className="mt-2 text-gray-600">Set up a new promotional offer for your products</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Details Section */}
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900 pb-2 border-b">
              Basic Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Offer Name - Modern Input */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Offer Name *
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiTag className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="block w-full pl-10 pr-3 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder="e.g., Summer Sale 2024"
                  />
                </div>
              </div>

              {/* Offer Type - Modern Select */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Offer Type *
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {formData.offerType === 'PERCENTAGE' ? (
                      <FiPercent className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                    ) : (
                      <FiDollarSign className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                    )}
                  </div>
                  <select
                    required
                    value={formData.offerType}
                    onChange={(e) => setFormData({ ...formData, offerType: e.target.value })}
                    className="block w-full pl-10 pr-10 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 appearance-none"
                  >
                    <option value="PERCENTAGE">Percentage Discount</option>
                    <option value="FIXED">Fixed Amount</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <FiChevronDown className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Discount Value - Modern Input */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Discount Value *
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {formData.offerType === 'PERCENTAGE' ? (
                      <FiPercent className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                    ) : (
                      <FiDollarSign className="h-5 w-5 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                    )}
                  </div>
                  <input
                    type="number"
                    required
                    min="0"
                    max={formData.offerType === 'PERCENTAGE' ? "100" : undefined}
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    className="block w-full pl-10 pr-3 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    placeholder={formData.offerType === 'PERCENTAGE' ? "Enter percentage" : "Enter amount"}
                  />
                </div>
              </div>

              {/* Date Range - Modern DatePicker */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Start Date *
                </label>
                <div className="relative datepicker-icon-container">
                  <FiCalendar className="datepicker-icon" />
                  <DatePicker
                    selected={formData.startDate}
                    onChange={(date) => setFormData({ ...formData, startDate: date })}
                    selectsStart
                    startDate={formData.startDate}
                    endDate={formData.endDate}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="Select start date and time"
                    className="w-full pl-10 pr-3 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    wrapperClassName="w-full"
                    calendarClassName="shadow-lg"
                    showPopperArrow={false}
                    autoComplete="off"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  End Date *
                </label>
                <div className="relative datepicker-icon-container">
                  <FiCalendar className="datepicker-icon" />
                  <DatePicker
                    selected={formData.endDate}
                    onChange={(date) => setFormData({ ...formData, endDate: date })}
                    selectsEnd
                    startDate={formData.startDate}
                    endDate={formData.endDate}
                    minDate={formData.startDate}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MMM d, yyyy h:mm aa"
                    placeholderText="Select end date and time"
                    className="w-full pl-10 pr-3 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200"
                    wrapperClassName="w-full"
                    calendarClassName="shadow-lg"
                    showPopperArrow={false}
                    autoComplete="off"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Products Section with new MultiSelect UI */}
          <div className="space-y-6">
            <h2 className="text-lg font-medium text-gray-900 pb-2 border-b">
              Product Selection
            </h2>
            
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Products *
              </label>
              <div className="relative">
                <div
                  className="min-h-[42px] px-3 py-2 border border-gray-300 rounded-md shadow-sm cursor-pointer bg-white"
                  onClick={() => setIsProductSelectorOpen(!isProductSelectorOpen)}
                >
                  <div className="flex flex-wrap gap-2">
                    {getSelectedProducts().length > 0 ? (
                      getSelectedProducts().map(product => (
                        <span
                          key={product.id}
                          className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-indigo-100 text-indigo-800"
                        >
                          {product.name}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProductToggle(product.id);
                            }}
                            className="ml-1 hover:text-indigo-500"
                          >
                            <FiX className="h-4 w-4" />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500">Select products...</span>
                    )}
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <FiChevronDown
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                        isProductSelectorOpen ? 'transform rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>

                {/* Dropdown Panel */}
                {isProductSelectorOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="text"
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="Search products..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>

                    {/* Products List */}
                    <div className="max-h-60 overflow-auto">
                      {filteredProducts.length > 0 ? (
                        filteredProducts.map(product => (
                          <div
                            key={product.id}
                            className={`
                              px-4 py-2 cursor-pointer hover:bg-gray-50 flex items-center justify-between
                              ${formData.selectedItems.includes(product.id) ? 'bg-indigo-50' : ''}
                            `}
                            onClick={() => handleProductToggle(product.id)}
                          >
                            <span className="text-sm text-gray-900">{product.name}</span>
                            {formData.selectedItems.includes(product.id) && (
                              <span className="text-indigo-600">
                                <FiX className="h-4 w-4" />
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          No products found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {formData.selectedItems.length > 0 && (
                <p className="mt-2 text-sm text-gray-500">
                  {formData.selectedItems.length} product{formData.selectedItems.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          </div>

          {/* Submit Button - Modern Styling */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/admin/offers')}
              className="px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2.5 text-white bg-indigo-600 rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <FiLoader className="animate-spin h-5 w-5" />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <FiTag className="h-5 w-5" />
                  <span>Create Offer</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default CreateOffer;









